const blog = (function () {
  // short-circuit execution on other templates
  if (document.body.dataset.templateName !== "blog") return;

  const PROJECTS_PER_PAGE = 40;

  const state = {
    view: null,
    tag: null,
    articlesByTag: [],
    currentPage: null,
    room_tags: [],
    material_tags: [],
    allArticlesLoaded: false,
  };

  let lazyLoadingEnabled = false;
  let galleryData = {};
  let imageLoad = null;
  let masonry = null;

  const selectors = {
    viewToggle: "[data-view-toggle]",
    viewButton: "[data-view]",
    tagNav: "[data-tag-nav]",
    tagLinklist: "[data-tag-linklist]",
    tagLink: "[data-tag-link]",
    nextPrevLink: "[data-next-prev-link]",
    grid: "[data-gallery-grid]",
    gridSizer: ".grid-sizer",
    gridBottom: "[data-grid-bottom]",
    lazyicon: "[data-load-more]",
  };

  // for caching DOM elements
  const dom = {};
  const cacheDom = () => {
    dom.viewToggle = document.querySelector(selectors.viewToggle);
    dom.viewButtons = Array.from(
      document.querySelectorAll(selectors.viewButton)
    );
    dom.tagNav = document.querySelector(selectors.tagNav);
    dom.tagLinklists = Array.from(
      document.querySelectorAll(selectors.tagLinklist)
    );
    dom.tagLinks = Array.from(document.querySelectorAll(selectors.tagLink));
    dom.grid = document.querySelector(selectors.grid);
    dom.gridBottom = document.querySelector(selectors.gridBottom);
  };

  const fetchAllProjects = () => {
    fetch("/blogs/galleries?view=json")
      .then((blob) => blob.json())
      .then((response) => {
        galleryData = response;
        urlFallbackChecks();
        renderPage();
        createIntersectionObservers();
      });
  };

  // set up state from initial URL
  const initPageState = () => {
    let view, tag;
    const url = window.location.href;
    const blogPath = url.slice(url.indexOf("/blogs/galleries"));
    if (blogPath.includes("/tagged/")) {
      const viewTagPath = blogPath.split("/tagged/")[1];
      // room view
      if (viewTagPath.includes("room-")) {
        view = "room";
        tag = viewTagPath.replace("room-", "");
      } else if (viewTagPath.includes("material-")) {
        // material view
        view = "material";
        tag = viewTagPath.replace("material-", "");
      }
    }

    // get all current room and material tags in the blog
    let room_tags = [];
    let material_tags = [];
    const viewNavs = Array.from(
      document.querySelectorAll(selectors.tagLinklist)
    );
    viewNavs.forEach((nav) => {
      const view = nav.dataset.tagLinklist;
      const tags = Array.from(nav.querySelectorAll(selectors.tagLink)).map(
        (el) => el.dataset.tagLink
      );
      if (view === "room") {
        room_tags = tags;
      } else if (view === "material") {
        material_tags = tags;
      }
    });

    state.room_tags = room_tags;
    state.material_tags = material_tags;

    state.view = view;
    state.tag = tag;
    // scroll to top (prevents sticky header issues)
    window.scrollTo(0, 0);
  };

  /*
      this function is called once on page load
      checks for valid URL and provides fallback URL + empty blog message
    */
  const urlFallbackChecks = () => {
    const { view, tag } = state;
    let updatedView = view;
    let updatedTag = tag;

    // if there's no view found in URL, update to 'room' view
    if (!updatedView) {
      updatedView = "room";
      updatedTag = getFirstAvailableLinkTag("room");

      if (!updatedTag) {
        // if there are no projects with room tags
        // check available tags on the 'material' view
        updatedView = "material";
        updatedTag = getFirstAvailableLinkTag("material");
        console.log("updating to material view with tag: ", updatedTag);

        if (!updatedTag) {
          // there are no room / material tags in the entire blog
          // show 'no projects found'
          document.querySelector(
            selectors.grid
          ).innerHTML = `<p class="no-projects">No Gallery Projects found</p>`;
          return;
        }
      }
    }
    // set fallback state
    state.view = updatedView;
    state.tag = updatedTag;
    // update URL + page if there was no /tagged/ URL
    if (!view) {
      const tagString = `${state.view}-${state.tag}`;
      const updatedUrl = `${
        window.location.href.split("/galleries")[0]
      }/galleries/tagged/${tagString}`;
      window.history.replaceState({}, "", updatedUrl);
    }
  };

  // sets up the appropriate metadata for SEO purposes before rendering page
  const updateDocumentTitle = () => {
    let { view, tag } = state;
    view = view ? `${view.charAt(0).toUpperCase()}${view.slice(1)}` : ""; // capitalize first letter
    tag = tag
      ? tag
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : undefined;
    document.title = `Galleries By ${view} ${tag ? ` - ${tag}` : ""}`;
  };

  // get first in-order tag item based on available tags + view
  const getFirstAvailableLinkTag = (view) => {
    const tags = galleryData.all_tags;
    let tag;
    if (view === "room") {
      // room link order: kitchen, bathroom, commercial, living spaces
      if (tags.includes("room-kitchen")) {
        tag = "kitchen";
      } else if (tags.includes("room-bathroom")) {
        tag = "bathroom";
      } else if (tags.includes("room-commercial")) {
        tag = "commercial";
      } else if (tags.includes("room-living-spaces")) {
        tag = "living-spaces";
      }
    } else {
      // material link order: stone, glass, porcelain, ceramic
      if (tags.includes("material-kitchen")) {
        tag = "kitchen";
      } else if (tags.includes("material-stone")) {
        tag = "stone";
      } else if (tags.includes("material-porcelain")) {
        tag = "porcelain";
      } else if (tags.includes("material-ceramic")) {
        tag = "ceramic";
      }
    }

    return tag;
  };

  const setActiveViewButton = (view) => {
    const viewButtons = dom.viewButtons
      ? dom.viewButtons
      : document.querySelectorAll(selectors.viewButton);
    // set active state on toggle
    viewButtons.forEach((b) => b.classList.remove("active"));
    viewButtons.find((b) => b.dataset.view === view).classList.add("active");
  };

  const toggleLinkNavsByView = (view) => {
    const tagLinklists = dom.tagLinklists
      ? dom.tagLinklists
      : document.querySelectorAll(selectors.tagLinklist);
    // swap out the tag link list
    tagLinklists.forEach((b) => b.classList.add("visually-hidden"));
    tagLinklists
      .find((b) => b.dataset.tagLinklist === view)
      .classList.remove("visually-hidden");
  };

  const setActiveTagLink = (tag) => {
    const tagLinks = dom.tagLinks
      ? dom.tagLinks
      : document.querySelectorAll(selectors.tagLink);
    // swap out the tag link list
    tagLinks.forEach((t) => t.classList.remove("active"));
    tagLinks.find((t) => t.dataset.tagLink === tag).classList.add("active");
  };

  const updateGrid = (tagString) => {
    const currentFilteredArticles = state.articlesByTag;
    // all articles with current tag
    const newFilteredArticles = galleryData.articles.filter((article) =>
      article.tags.includes(tagString)
    );

    // check if there are updates to make on the filtered articles
    const diffArray1 = currentFilteredArticles.filter(
      ({ id: a }) => !newFilteredArticles.some(({ id: b }) => b === a)
    );
    const diffArray2 = newFilteredArticles.filter(
      ({ id: a }) => !currentFilteredArticles.some(({ id: b }) => b === a)
    );
    // no changes needed -- return
    if (!diffArray1.length && !diffArray2.length) return;
    // update state variable + re-render
    state.articlesByTag = newFilteredArticles;

    // reset loaded state - do we need paginated loading again?
    state.allArticlesLoaded = state.articlesByTag.length < PROJECTS_PER_PAGE;

    // show first 'page' of fake pagination
    const firstPageOfArticles = [...newFilteredArticles].splice(
      0,
      PROJECTS_PER_PAGE
    );
    state.currentPage = 1;

    const container = dom.grid
      ? dom.grid
      : document.querySelector(selectors.grid);
    renderGrid(container, firstPageOfArticles);
  };

  const appendToGrid = () => {
    state.currentPage++;
    const updatedSetOfArticles = [...state.articlesByTag].splice(
      0,
      state.currentPage * PROJECTS_PER_PAGE
    );
    if (updatedSetOfArticles.length === state.articlesByTag.length) {
      state.allArticlesLoaded = true;
    }
    renderGrid(dom.grid, updatedSetOfArticles);
  };

  const renderGrid = (container, articles) => {
    // setup a grid-sizer for Masonry to work responsively
    container.innerHTML = '<div class="grid-sizer"></div>';
    articles.forEach((article) => {
      const projectTile = buildProjectTile(article);
      container.appendChild(projectTile);
    });

    // setup new imagesLoaded listener
    imageLoad = imagesLoaded(container);
    // add 'loaded' class to tiles when its image is loaded
    imageLoad.on("progress", (instance, imageObj) => {
      // fade in tile when image loaded
      imageObj.img.closest(".project-tile").classList.add("loaded");
    });
    // wait until all images loaded before triggering Masonry
    imageLoad.on("always", (instance) => {
      $(selectors.lazyicon).show();
      setTimeout(function () {
        $(selectors.lazyicon).hide();
      }, 500);
      refreshMasonry(articles);
    });
  };

  const buildProjectTile = (article) => {
    const projectElement = document.createElement("article");
    projectElement.classList.add("project-tile");
    // set width and height for masonryJS to use in layout
    // projectElement.style.cssText = `width: ${state.columnWidth}px; height: ${state.columnWidth / Number(article.aspect_ratio)}px;`;
    // projectElement.setAttribute('data-lazyload', '');
    projectElement.innerHTML = `<a href="${article.url}">
        <div class="project-tile__image-container">
          <img class="project-tile__image" ${
            lazyLoadingEnabled
              ? `data-image-src="${article.image}"`
              : `src="${article.image}"`
          } alt="${state.tag[0].toUpperCase() + state.tag.slice(1)} - ${article.title}"
          />
          <h2 class="project-tile__title desktop">${article.title}</h2>
          ${
            article.multiple_images
              ? '<span class="project-tile__more-images-icon"><img src="https://cdn.shopify.com/s/files/1/0272/6956/2456/files/ART-Icon-MoreImages_1.svg?v=1606203479" alt=""></span>'
              : ""
          }
        </div>
        <h2 class="project-tile__title mobile">${article.title}</h2>
      </a>`;

    return projectElement;
  };

  const renderPage = () => {
    const { view, tag } = state;
    const tagString = `${view}-${tag}`;
    // update title tag for SEO
    updateDocumentTitle();
    // update nav menus
    setActiveViewButton(view);
    toggleLinkNavsByView(view);
    setActiveTagLink(tag);
    // update tile grid
    updateGrid(tagString);
    // update URL to match new view
    const updatedUrl = `${
      window.location.href.split("/galleries")[0]
    }/galleries/tagged/${tagString}`;
    window.history.replaceState({}, "", updatedUrl);

    // update next + prev links
    updateNextPrev(view, tag);
  };

  const initMasonry = () => {
    masonry = new Masonry(selectors.grid, {
      columnWidth: selectors.gridSizer,
      itemSelector: ".project-tile",
      percentPosition: true,
      //   fitWidth: true,
    });
  };

  const onResize = debounce(() => {
    if (masonry) {
      masonry._init();
    }
  }, 200);

  window.addEventListener("resize", onResize);

  // setup masonry on first load, then refresh when the grid changes
  const refreshMasonry = (articles) => {
    if (!masonry) {
      initMasonry();
    } else {
      // refresh the masonry layout when view/tag changes
      masonry.destroy();
      masonry = null;
      initMasonry();
    }
    masonry.layout();
  };

  // intersection observer for "pagination" + lazy loading
  const createIntersectionObservers = () => {
    // check if the feature is available in browser
    if ("IntersectionObserver" in window) {
      const gridObserver = new IntersectionObserver(
        (entries, gridObserver) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !state.allArticlesLoaded) {
              // append more projects to grid when the bottom of the grid is in the viewport
              appendToGrid();
            }
          });
        },
        { threshold: 1 }
      );

      const gridBottom = dom.gridBottom
        ? dom.gridBottom
        : document.querySelector(selectors.gridBottom);
      gridObserver.observe(gridBottom);

      // TODO: bind each project tile when they are loaded, or refresh the observer
      // const imageObserver = ...
    }
  };

  // update content of previous/next gallery links
  const updateNextPrev = (view, tag) => {
    const prevLink = document.querySelector(
      `.gallery-item.prev ${selectors.nextPrevLink}`
    );
    const nextLink = document.querySelector(
      `.gallery-item.next ${selectors.nextPrevLink}`
    );
    const prevLinkTitle = prevLink.querySelector(".gallery-item-title");
    const nextLinkTitle = nextLink.querySelector(".gallery-item-title");

    // we need to get current tags in case the client removes all of a tag type
    const tagsByView = view === "room" ? state.room_tags : state.material_tags;
    const currentIndex = tagsByView.indexOf(tag);

    const prevTag = tagsByView[currentIndex - 1];
    const nextTag = tagsByView[currentIndex + 1];
    const Gallery = "Gallery";

    // const elmnt = document.querySelector(".project-category-nav");
    // const scroll_bar = elmnt.scrollWidth;
    // if(scroll_bar > window.outerWidth){
    //   document.querySelector('.list-right-arrow').classList.add('display');
    //   document.querySelector('.list-left-arrow').classList.add('display');
    // }
    // else{
    //   document.querySelector('.list-right-arrow').classList.remove('display');
    //   document.querySelector('.list-left-arrow').classList.remove('display');
    // }
    // update previous button content
    if (prevTag) {
      prevLinkTitle.textContent = `${prevTag.replace(/-/g, " ")} ${Gallery}`;
      prevLink.dataset.nextPrevLink = prevTag;
      prevLink.setAttribute(
        "href",
        `/blogs/galleries/tagged/${view}-${prevTag}`
      );
      prevLink.classList.remove("no-link");
      document
        .querySelector(".gallery-item.prev")
        .classList.remove("prev-hidden");
      document
        .querySelector(".gallery-item.next")
        .classList.remove("next-border-hidden");
    } else {
      // remove content + hide
      $(".gallery-item.prev").addClass("prev-hidden");
      prevLinkTitle.textContent = "";
      prevLink.dataset.nextPrevLink = "";
      prevLink.setAttribute("href", "");
      prevLink.classList.add("no-link");
      document.querySelector(".gallery-item.prev").classList.add("prev-hidden");
      document
        .querySelector(".gallery-item.next")
        .classList.add("next-border-hidden");
    }

    // update next button content
    if (nextTag) {
      nextLinkTitle.textContent = `${nextTag.replace(/-/g, " ")} ${Gallery}`;
      nextLink.dataset.nextPrevLink = nextTag;
      nextLink.setAttribute(
        "href",
        `/blogs/galleries/tagged/${view}-${nextTag}`
      );
      nextLink.classList.remove("no-link");
      document
        .querySelector(".gallery-item.next")
        .classList.remove("next-hidden");
    } else {
      // remove content + hide
      nextLinkTitle.textContent = "";
      nextLink.dataset.nextPrevLink = "";
      nextLink.setAttribute("href", "");
      nextLink.classList.add("no-link");
      document.querySelector(".gallery-item.next").classList.add("next-hidden");
    }
  };

  const bindUIActions = () => {
    document.addEventListener("click", (e) => {
      if (e.target.matches(selectors.viewButton)) {
        // toggle view
        const view = e.target.dataset.view;
        if (view !== state.view) {
          state.view = view;
          state.tag = getFirstAvailableLinkTag(view);
          renderPage();
        }
      } else if (e.target.matches(`${selectors.tagLink} a`)) {
        // update tag filter
        e.preventDefault(); // prevent page reload
        const tag = e.target.parentElement.dataset.tagLink;
        if (tag !== state.tag) {
          state.tag = tag;
          renderPage();
        }
      }
      // else if (e.target.matches('.list-right-arrow2')) {
      //   $('.project-category-nav ul').find('li.active').next().trigger('click');

      // }
      // else if (e.target.matches('.list-left-arrow2')) {
      //   $('.project-category-nav ul').find('li.active').prev().trigger('click');
      // }
      else if (e.target.matches(".roomtags")) {
        e.preventDefault(); // prevent page reload
        const tag = e.target.dataset.tagLink;
        if (tag !== state.tag) {
          state.tag = tag;
          renderPage();
        }
      }
      // uncomment this to update page without reload from the next/prev links
      // else if (e.target.matches(selectors.nextPrevLink) || e.target.matches(`${selectors.nextPrevLink} *`)) {
      //   // update tag filter
      //   e.preventDefault(); // prevent page reload
      //   const tag = e.target.closest(selectors.nextPrevLink).dataset.nextPrevLink;
      //   if(tag !== state.tag) {
      //     state.tag = tag;
      //     renderPage();
      //   }
      // }
    });

    //  // window History API
    //   window.addEventListener('popstate', e => {
    //     // grab state data from object in History
    //     const { view, tag } = e.state;
    //     // set in page state + refresh
    //     if(view && tag) {
    //       state.view = view;
    //       state.tag = tag;
    //       renderPage();
    //     }
    //   });
  };

  const init = () => {
    if (!window.location.href.includes("/galleries")) return;
    initPageState();
    fetchAllProjects();
    cacheDom();
    bindUIActions();
  };

  return {
    init: init,
  };
})();
